import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal, Spin } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection } from '../../connection/connection';
import { Vendor, VendorAddress } from '../../dto/Vendor';
import { useEvents } from '../../common/EventContext';
import { setFormValidationErrors, Utils } from '../../common/Utils';
import { useBusinessContext } from '../../context/BusinessContext';
import { VendorAddressSection } from './VendorAddressSection';
import { Item } from '../../dto/Item';
import { getCurrencies } from '../../common/SelectEnums';

export type VendorEditModalInstance = {
  open: (name?: string) => void;
  edit: (vendorId: number) => void;
};

type Props = {};

const VendorEditModal: ForwardRefRenderFunction<
  VendorEditModalInstance,
  Props
> = (props, ref) => {
  const [title, setTitle] = useState<string>('Create Vendor');
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [form] = Form.useForm<Vendor>();
  const events = useEvents();
  const connection = useConnection();
  const [nameOptions, setNameOptions] = useState<string[]>([]);
  const [billingAddress, setBillingAddress] = useState<VendorAddress>();
  const business = useBusinessContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [existingVendor, setExistingVendor] = useState<Vendor>();

  useImperativeHandle(
    ref,
    () => ({
      open: (name?: string) => {
        setBillingAddress(undefined);
        setNameOptions([]);
        setVisible(true);
        setSaving(false);
        setExistingVendor(undefined);
        form.resetFields();
        alert(name);
        form.setFieldsValue({ currency: business.currency, name: name });
      },
      edit: (vendorId: number) => {
        setBillingAddress(undefined);
        setNameOptions([]);
        setVisible(true);
        setSaving(false);
        setLoading(true);
        form.resetFields();
        form.setFieldsValue({ currency: business.currency });
        connection
          .get<Vendor>(`${business.businessId}/vendors/${vendorId}`)
          .then((res) => {
            form.setFieldsValue(res.data);
            setExistingVendor(res.data);
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            Modal.error({ title: 'Error', content: 'Something went wrong!' });
            setVisible(false);
          });
      },
    }),
    [business.businessId, business.currency, connection, form],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'salutation',
        type: 'select',
        name: 'Salutation',
        layout: { xs: 4 },
        props: {
          options: ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.', 'Rev.'],
          placeholder: 'Salutation',
          allowClear: true,
        },
        options: {
          rules: [{ required: true, message: 'Salutation is required' }],
        },
      },
      {
        id: 'firstName',
        type: 'string',
        name: 'First Name',
        layout: { xs: 10 },
        props: { placeholder: 'First Name' },
        options: {
          rules: [{ required: true, message: 'First name is required' }],
        },
      },
      {
        id: 'lastName',
        type: 'string',
        name: 'Last Name',
        layout: { xs: 10 },
        props: { placeholder: 'Last Name' },
        options: {
          rules: [{ required: true, message: 'Last name is required' }],
        },
      },
      {
        id: 'company',
        type: 'string',
        name: 'Company',
        props: { placeholder: 'Company' },
        options: {
          rules: [{ required: false, message: 'Company is required' }],
        },
      },
      {
        id: 'name',
        type: 'autocomplete',
        name: 'Name',
        props: {
          options: nameOptions.map((value) => ({ value: value, label: value })),
          placeholder: 'Name',
          allowClear: true,
        },
        options: {
          rules: [{ required: true, message: 'Name is required' }],
        },
      },
      {
        id: 'email',
        type: 'email',
        name: 'Email',
        props: { placeholder: 'Email' },
        options: {
          rules: [{ validator: 'email', message: 'Invalid email' }],
        },
      },
      {
        id: 'phone',
        type: 'phone',
        name: 'Phone',
        layout: { xs: 12 },
        props: { placeholder: 'Phone', defaultCountry: 'LK' },
        options: {
          rules: [{ validator: 'phone', message: 'Invalid phone number' }],
        },
      },
      {
        id: 'mobile',
        type: 'phone',
        name: 'Mobile',
        layout: { xs: 12 },
        props: { placeholder: 'Mobile', defaultCountry: 'LK' },
        options: {
          rules: [{ validator: 'phone', message: 'Invalid phone number' }],
        },
      },
      {
        id: 'currency',
        type: 'select2',
        name: 'Currency',
        props: { placeholder: 'Currency', options: getCurrencies(business) },
        options: {
          rules: [{ required: true, message: 'Currency is required' }],
        },
      },
      {
        id: 'billingAddress',
        type: 'address',
        name: 'Billing Address',
      },
      {
        id: 'shippingAddress',
        type: 'address',
        name: 'Shipping Address',
        props: { linked: true, linkedValue: billingAddress },
      },
      {
        id: 'notes',
        type: 'text',
        name: 'Notes',
      },
      {
        id: 'contactPersons',
        type: 'contact',
        name: 'Contact Persons',
      },
    ];
    return formSchema;
  }, [billingAddress, nameOptions, business]);

  const submit = useCallback(
    (values: Vendor) => {
      setSaving(true);
      if (existingVendor) {
        connection
          .patch<Vendor>(
            `${business.businessId}/vendors/${existingVendor.vendorId}`,
            values,
          )
          .then((res) => {
            events.emit('VENDOR', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .post<Vendor>(`${business.businessId}/vendors`, values)
          .then((res) => {
            events.emit('VENDOR', { action: 'CREATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      }
    },
    [business.businessId, connection, events, existingVendor, form],
  );

  const onFormValueChange = useCallback(
    (changed: Partial<Vendor>, value: Partial<Vendor>) => {
      if (
        Object.hasOwn(changed, 'salutation') ||
        Object.hasOwn(changed, 'firstName') ||
        Object.hasOwn(changed, 'lastName') ||
        Object.hasOwn(changed, 'company')
      ) {
        let options: string[] = [];
        const salutationAvailable = Utils.isNotEmptyString(value, 'salutation');
        const firstNameAvailable = Utils.isNotEmptyString(value, 'firstName');
        const lastNameAvailable = Utils.isNotEmptyString(value, 'lastName');
        const companyAvailable = Utils.isNotEmptyString(value, 'company');
        if (firstNameAvailable || lastNameAvailable) {
          if (firstNameAvailable && lastNameAvailable) {
            options.push(`${value.firstName} ${value.lastName}`);
            options.push(`${value.lastName}, ${value.firstName}`);
          } else if (firstNameAvailable) {
            options.push(`${value.firstName}`);
          } else {
            options.push(`${value.lastName}`);
          }

          if (salutationAvailable) {
            options = options.map((o) => `${value.salutation} ${o}`);
          }
        }
        if (companyAvailable) {
          options.push(value.company!);
        }
        setNameOptions(options);
      }
      if (Object.hasOwn(changed, 'billingAddress')) {
        setBillingAddress({ ...changed.billingAddress, linked: true });
      }
    },
    [],
  );

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          <div className="w-full flex justify-end gap-1">
            <Button onClick={() => setVisible(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={form.submit}>
              OK
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Spin spinning={loading}>
        <Form
          onFinish={submit}
          form={form}
          layout="vertical"
          onValuesChange={onFormValueChange}
        >
          <Form.Items schema={schema} />
        </Form>
      </Spin>
    </Drawer>
  );
};

export const useVendorEditModal = () => useRef<VendorEditModalInstance>(null);

export default React.forwardRef<VendorEditModalInstance, Props>(
  VendorEditModal,
);
